import { Audio } from 'expo-av';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Logger } from '../../../utils/Logger';
import { IAppStore } from '../../reducer';
import { genericMusicError, genericMusicSkipToNext } from './actions';
import { currentPlayingTrackUrlSelector, genericMusicIsPausedSelector, genericMusicVolumeSelector } from './selectors';

interface IProps {
  currentPlayingUrl?: string;
  isPaused: boolean;
  genericMusicVolume: number;
  genericMusicSkipToNext(): void;
  genericMusicError(error: string): void;
}

const PLAYBACK_SPEED_USEFUL_FOR_TESTING = 1; // 1 - 32

class GenericMusicComponent extends PureComponent<IProps> {
  private sound?: Audio.Sound;

  public async componentDidMount() {
    if (this.props.currentPlayingUrl) {
      await this.loadAndPlay();
    }
  }

  public async componentDidUpdate(previousProps: IProps) {
    if (this.shouldStop()) {
      await this.unloadLastTrack();
      return;
    }

    if (this.shouldSongChange(previousProps)) {
      await this.loadAndPlay();
    }

    if (!this.sound) {
      return;
    }

    const status: any = await this.sound.getStatusAsync();
    if (this.shouldPause(status)) {
      await this.sound.pauseAsync();
    }

    if (this.shouldPlay(status)) {
      await this.safelyPlayCurrentSongAtCorrectVolume();
    }

    if (this.shoulChangeVolume(previousProps)) {
      this.seekToVolume(this.props.genericMusicVolume);
    }
  }

  public async componentWillUnmount() {
    await this.unloadLastTrack();
  }

  public render() {
    return <></>;
  }

  private async loadAndPlay() {
    if (this.sound && this.sound) {
      await this.unloadLastTrack();
    }

    this.sound = new Audio.Sound();
    this.sound.setOnPlaybackStatusUpdate(this.playbackStatusUpdate);
    await this.sound.loadAsync({
      uri: this.props.currentPlayingUrl,
    });
    await this.safelyPlayCurrentSongAtCorrectVolume();
  }

  private async unloadLastTrack() {
    if (!this.sound) {
      return;
    }

    try {
      await this.sound.stopAsync();
      await this.sound.unloadAsync();
    } catch (e) {
      Logger.leaveBreadcrumb('Failed to unload generic music', {
        fullError: JSON.stringify(e),
      });
    } finally {
      delete this.sound;
    }
  }

  private playbackStatusUpdate = (status: any) => {
    if (status.didJustFinish) {
      this.props.genericMusicSkipToNext();
    }

    if (status.error) {
     this.props.genericMusicError(status.error);
    }
  }

  private seekToVolume = (wantedVolume) => {
    if (!this.sound) {
      return;
    }

    const adjustVolume = async  () => {
      const status: any = await this.sound?.getStatusAsync();
      if (!status) {
        return;
      }

      const currentVolume = status.volume;
      if (currentVolume === undefined) {
        return;
      }

      if (currentVolume.toFixed(1) !== wantedVolume.toFixed(1)) {
        const adjustment = currentVolume < wantedVolume ? 0.1 : -0.1;
        const newVolume = currentVolume + adjustment;
        const volumeWithingBounds = newVolume < 0 ? 0 : newVolume > 1 ? 1 : newVolume;
        await this.sound?.setVolumeAsync(volumeWithingBounds);
        setTimeout(() => {
          adjustVolume()
            .catch((e) => Logger.notify(e));
        }, 50);
      }
    };
    adjustVolume()
      .catch((e) => Logger.notify(e));
  }

  private async safelyPlayCurrentSongAtCorrectVolume() {
    if (!this.sound) {
      return;
    }

    const status = await this.sound.getStatusAsync();
    if (status.isLoaded) {
      await this.sound.setVolumeAsync(this.props.genericMusicVolume);
      await this.sound.playAsync();
      await this.sound.setRateAsync(PLAYBACK_SPEED_USEFUL_FOR_TESTING, false);
    }
  }

  private shoulChangeVolume(previousProps: IProps) {
    return previousProps.genericMusicVolume !== this.props.genericMusicVolume;
  }

  private shouldPlay(status: any) {
    return !this.props.isPaused && !status.isPlaying;
  }

  private shouldPause(status: any) {
    return this.props.isPaused && status.isPlaying;
  }

  private shouldSongChange(previousProps: IProps) {
    return previousProps.currentPlayingUrl !== this.props.currentPlayingUrl;
  }

  private shouldStop() {
    return !this.props.currentPlayingUrl;
  }
}

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    currentPlayingUrl: currentPlayingTrackUrlSelector(state),
    isPaused: genericMusicIsPausedSelector(state),
    genericMusicVolume: genericMusicVolumeSelector(state),
  };
};

const mapDispatchToProps = (dispatch): Partial<IProps> => {
  return {
    genericMusicSkipToNext: () => dispatch(genericMusicSkipToNext()),
    genericMusicError: (error: string) => dispatch(genericMusicError(error)),
  };
};

export const GenericMusic = connect(mapStateToProps, mapDispatchToProps)
  (GenericMusicComponent);
